import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2, ChevronDown, ChevronRight, EyeOff, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import { RoleBadge } from '@/features/organizations/components/role-badge/RoleBadge';
import { supplyPointQueryKeys } from '@/features/supply-points/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';
import { useAddOrgDialog } from '../add-org-dialog/useAddOrgDialog';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { avatarUrl } from '@/common/utils/avatarUrl';
import type { OrgWithMembers } from '@/features/supply-points/services/schemas';

function OrgAccessSubtitle({ orgAccess }: { orgAccess: OrgWithMembers }) {
  if (!orgAccess.membersVisible) {
    return <Trans>You don&apos;t have permission to view members of this organization</Trans>;
  }
  const count = orgAccess.members.length;
  return <Trans>{count} members · access via organization</Trans>;
}

type Props = {
  supplyPointId: number;
  orgAccesses: OrgWithMembers[];
  search: string;
};

export function OrganizationsTab({ supplyPointId, orgAccesses, search }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const openAddOrgDialog = useAddOrgDialog();

  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());

  const removeOrgAccessMutation = useMutation({
    mutationFn: (partnerId: number) =>
      SupplyPointService.removePartners(supplyPointId, [partnerId]),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all }),
  });

  const handleRemoveOrgAccess = async (id: number, orgName: string) => {
    const confirmed = await confirm({
      title: t`Remove organization?`,
      description: t`Removing "${orgName}" will revoke access for all its members.`,
      confirmLabel: t`Remove`,
    });
    if (confirmed) removeOrgAccessMutation.mutate(id);
  };

  const toggleOrgExpanded = (id: number) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredOrgAccesses = orgAccesses.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  const alreadyAddedPartnerIds = orgAccesses.map((o) => o.id);

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
        <p className="text-sm text-muted-foreground">
          {orgAccesses.length} <Trans>organizations linked</Trans>
        </p>
        <Button
          onClick={() => openAddOrgDialog(supplyPointId, alreadyAddedPartnerIds)}
          className="gap-1.5"
          size="sm"
        >
          <Plus size={14} />
          <Trans>Add organization</Trans>
        </Button>
      </div>

      {filteredOrgAccesses.length === 0 ? (
        <div className="py-16 text-center">
          <Building2 size={32} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">
            {search ? (
              <Trans>No organizations found</Trans>
            ) : (
              <Trans>No organizations have been added yet</Trans>
            )}
          </p>
          {!search && (
            <Button
              onClick={() => openAddOrgDialog(supplyPointId, alreadyAddedPartnerIds)}
              className="mt-4 gap-2"
              size="sm"
            >
              <Plus size={14} />
              <Trans>Add organization</Trans>
            </Button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {filteredOrgAccesses.map((orgAccess) => {
            const isExpanded = expandedOrgs.has(orgAccess.id);
            return (
              <div key={orgAccess.id}>
                <div className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                  {orgAccess.membersVisible ? (
                    <button
                      type="button"
                      onClick={() => toggleOrgExpanded(orgAccess.id)}
                      className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  ) : (
                    <span className="p-0.5 text-muted-foreground/40">
                      <EyeOff size={16} />
                    </span>
                  )}

                  <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-sm font-bold shrink-0">
                    {orgAccess.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{orgAccess.name}</span>
                      {!orgAccess.membersVisible && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground border border-border uppercase tracking-wide">
                          <Trans>Members hidden</Trans>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <OrgAccessSubtitle orgAccess={orgAccess} />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveOrgAccess(orgAccess.id, orgAccess.name)}
                    className="rounded-lg p-1.5 text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                {isExpanded && orgAccess.membersVisible && (
                  <div className="bg-muted/20 border-t border-border/50">
                    <div className="px-14 py-2">
                      <span className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                        <Trans>Members with inherited access</Trans>
                      </span>
                    </div>
                    <div className="divide-y divide-border/40">
                      {orgAccess.members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-3 px-14 py-2.5 hover:bg-muted/30 transition-colors"
                        >
                          <div className="size-7 rounded-full bg-muted overflow-hidden shrink-0 border border-border">
                            <img
                              src={avatarUrl(
                                member.user.firstName,
                                member.user.lastName,
                                member.user.email
                              )}
                              alt={
                                [member.user.firstName, member.user.lastName]
                                  .filter(Boolean)
                                  .join(' ') || member.user.email
                              }
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium">
                              {member.user.firstName} {member.user.lastName}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {member.user.email}
                            </span>
                          </div>
                          <RoleBadge role={member.role} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
